import React from 'react';
import { XIcon } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { ProvenanceBadge } from '../trust/ProvenanceBadge';
import { universityProfileById } from '../../data/admissions';
import { scholarships } from '../../data/admissions';
import { accommodationPressure } from '../../data/institution';
import type { Programme } from '../../types/admissions';
export function tuitionLabel(programme: Programme): string {
  if (programme.currency === 'NGN') {
    return `₦${programme.tuitionPerYear.toLocaleString()} / year`;
  }
  const symbol = programme.currency === 'EUR' ? '€' : '£';
  return `${symbol}${programme.tuitionPerYear.toLocaleString()} / year`;
}
interface CompareTrayProps {
  selected: Programme[];
  onRemove: (id: string) => void;
  onClear: () => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}
export function CompareTray({
  selected,
  onRemove,
  onClear,
  open,
  onOpenChange
}: CompareTrayProps) {
  if (selected.length === 0) return null;
  const rows: {
    label: string;
    render: (programme: Programme) => React.ReactNode;
  }[] = [{
    label: 'University',
    render: programme => universityProfileById(programme.universityId)?.name ?? '—'
  }, {
    label: 'Level',
    render: programme => programme.level
  }, {
    label: 'Field',
    render: programme => programme.field
  }, {
    label: 'Duration',
    render: programme => `${programme.durationMonths} months`
  }, {
    label: 'Tuition',
    render: programme => tuitionLabel(programme)
  }, {
    label: 'Delivery',
    render: programme => programme.deliveryMode
  }, {
    label: 'Deadline',
    render: programme => programme.deadlineLabel
  }, {
    label: 'Requirements',
    render: programme => <ul className="space-y-1 text-left" data-id="element-1175" data-mp="f0xky269yob5">
          {programme.requirements.map(requirement => <li key={requirement} data-id="element-1176" data-mp="azc2m1az1htk">· {requirement}</li>)}
        </ul>
  }, {
    label: 'Scholarships',
    render: programme => programme.scholarshipIds.length === 0 ? 'None recorded' : <ul className="space-y-1 text-left" data-id="element-1177" data-mp="2f3yiqegkfk9t">
            {programme.scholarshipIds.map(id => <li key={id} data-id="element-1178" data-mp="2k0ouwqp91vpy">
                · {scholarships.find(scholarship => scholarship.id === id)?.name ?? id}
              </li>)}
          </ul>
  }, {
    label: 'Housing nearby',
    render: programme => {
      const university = universityProfileById(programme.universityId);
      if (university?.country !== 'Nigeria') {
        return 'No Campuzon housing data for this city yet';
      }
      const pressure = accommodationPressure[0];
      return `${pressure.neighbourhood} and nearby — ${Math.round(pressure.demand / pressure.listedBeds * 10) / 10} students per listed bed`;
    }
  }, {
    label: 'Career outcomes',
    render: programme => programme.careerOutcomes.join(', ')
  }, {
    label: 'Data source',
    render: programme => <ProvenanceBadge provenance={programme.provenance} short data-id="element-1179" data-mp="113ll1ouesy6a" />
  }];
  return <>
      <div className="fixed bottom-16 left-0 right-0 z-20 border-t border-line bg-surface px-4 py-3 shadow-pop md:bottom-0 md:px-6" data-id="element-1180" data-mp="w4hb2vznuwew">
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-3" data-id="element-1181" data-mp="r67huqxhy5vx">
          <p className="cz-num text-[13px] font-semibold text-ink" data-id="element-1182" data-mp="1oto8frndpfyk">
            {selected.length} of 3 selected
          </p>
          <ul className="flex min-w-0 flex-1 flex-wrap gap-2" data-id="element-1183" data-mp="1jv4ofwscie8u">
            {selected.map(programme => <li key={programme.id} className="inline-flex items-center gap-1.5 rounded-full border border-line bg-canvas px-2.5 py-1 text-[12px] text-ink" data-id="element-1184" data-mp="1f21ydyms1mxe">
                {programme.title}
                <button type="button" onClick={() => onRemove(programme.id)} aria-label={`Remove ${programme.title} from comparison`} className="text-ink-subtle hover:text-ink" data-id="element-1185" data-mp="27lgzhgiw0r3n">
                  <XIcon className="h-3 w-3" data-id="element-1186" data-mp="1re2pcjbeivjz" />
                </button>
              </li>)}
          </ul>
          <div className="flex shrink-0 items-center gap-2" data-id="element-1187" data-mp="1deushx2pnziy">
            <Button size="sm" variant="ghost" onClick={onClear} data-id="element-1188" data-mp="1icwb6gw85wtw">
              Clear
            </Button>
            <Button size="sm" variant="primary" onClick={() => onOpenChange(true)} data-id="element-1189" data-mp="262qjcl5lmx7t">
              Compare
            </Button>
          </div>
        </div>
      </div>

      <Modal open={open} onClose={() => onOpenChange(false)} title="Compare programmes" description="Facts are labelled by source. Only institution-published data is marked verified." width="lg" footer={<Button variant="secondary" onClick={() => onOpenChange(false)} data-id="element-1191" data-mp="2oy6w4n0b9qh2">
            Close
          </Button>} data-id="element-1190" data-mp="1wc4cbfby7f0q">
        <div className="overflow-x-auto" data-id="element-1192" data-mp="ic1328v6us0s">
          <table className="w-full min-w-[560px] text-left align-top" data-id="element-1193" data-mp="iaq2sj0yc2ef">
            <thead data-id="element-1194" data-mp="ddg5yib8gogk">
              <tr className="border-b border-line" data-id="element-1195" data-mp="dbv74ghvyjcw">
                <th scope="col" className="w-32 px-2 py-2 text-[12px] uppercase tracking-wide text-ink-subtle" data-id="element-1196" data-mp="r4bh6197fhnb">
                  Field
                </th>
                {selected.map(programme => <th key={programme.id} scope="col" className="px-2 py-2 text-[13px] font-semibold text-ink" data-id="element-1197" data-mp="3vijao1xaeygg">
                    {programme.title}
                  </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-line" data-id="element-1198" data-mp="8e3hjt4pr9bv">
              {rows.map(row => <tr key={row.label} data-id="element-1199" data-mp="3e8pvnd46fpr">
                  <th scope="row" className="px-2 py-2.5 text-[12px] font-medium text-ink-muted" data-id="element-1200" data-mp="3d77extrqqy0">
                    {row.label}
                  </th>
                  {selected.map(programme => <td key={programme.id} className="px-2 py-2.5 text-[13px] text-ink" data-id="element-1201" data-mp="1etnoswovvhni">
                      {row.render(programme)}
                    </td>)}
                </tr>)}
            </tbody>
          </table>
        </div>
      </Modal>
    </>;
}